import { useEffect, useState } from "react";
import api from "../lib/api";
import Avatar from "../components/Avatar";
import FileUpload from "../components/FileUpload";
import { useAuth } from "../context/AuthContext";
import { Plus, UserPlus, Pause, Play, Trash2, Pencil, X, Save, KeyRound, Copy, Check, Loader2, Sparkles } from "lucide-react";

const SECTORS = ["Real Estate", "Legal & Corporate", "Finance & Investment", "Marketing & Brand", "Technology & Digital", "Construction", "Hospitality", "Wealth Management", "Operations", "Other"];
const GROUP_ROLES = ["Group Organizer", "Deal Coordinator", "Sector Expert", "Committed Member"];

export default function Settings() {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const load = () => api.get("/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);

  const runCleanup = async () => {
    if (!window.confirm("This will permanently remove every test-agent fixture (TEST_*, Push regression smoke, E2E *) from the database. Continue?")) return;
    setCleanupBusy(true);
    setCleanupResult(null);
    try {
      const { data } = await api.post("/admin/cleanup-test-data");
      setCleanupResult(data);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const update = async (id, body) => {
    await api.patch(`/members/${id}`, body);
    load();
  };
  const toggleStatus = (m) => update(m.id, { status: m.status === "active" ? "on_leave" : "active" });
  const setRole = (m, role) => update(m.id, { role });
  const setGroupRole = (m, group_role) => update(m.id, { group_role });

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/members/${confirmDelete.id}`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">Admin</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Settings</h1>
          <p className="text-[#9A9590] mt-3 max-w-xl">Manage the table. Invite, edit, put on leave, or remove members.</p>
        </div>
        <button onClick={() => setShowInvite(true)} className="btn-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm self-start" data-testid="invite-btn">
          <UserPlus size={16}/> Invite member
        </button>
      </div>

      <div className="ts-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#C9A84C]/15">
        <div className="flex items-start gap-3">
          <Sparkles size={18} className="text-[#C9A84C] mt-0.5"/>
          <div>
            <div className="text-sm text-[#F0EDE8] font-medium">Clean demo data</div>
            <div className="text-xs text-[#9A9590] mt-1 max-w-md">Removes accumulated QA fixtures (TEST_*, Push regression smoke, E2E *) and orphan notifications. Real members &amp; deals stay untouched.</div>
            {cleanupResult && (
              <div className="text-[11px] text-[#7CC274] mt-2" data-testid="cleanup-result">
                Removed {cleanupResult.users_removed} users, {cleanupResult.opportunities_removed} opportunities, {cleanupResult.deals_removed} deals, {cleanupResult.meetings_removed} meetings, {cleanupResult.notifications_removed} notifications.
              </div>
            )}
          </div>
        </div>
        <button
          onClick={runCleanup}
          disabled={cleanupBusy}
          data-testid="cleanup-test-data-btn"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10 transition rounded-md px-4 py-2 disabled:opacity-50"
        >
          {cleanupBusy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
          {cleanupBusy ? "Cleaning…" : "Clean now"}
        </button>
      </div>

      <div className="ts-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#9A9590] text-left">
            <tr>
              <th className="px-5 py-4">Member</th>
              <th className="px-5 py-4">Sector</th>
              <th className="px-5 py-4">Group role</th>
              <th className="px-5 py-4">Access</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="members-admin-table">
            {members.map((m) => {
              const isSelf = m.id === currentUser.id;
              return (
                <tr key={m.id} className="border-t border-[#C9A84C]/10 hover:bg-[rgba(201,168,76,0.02)]">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar name={m.name} photoUrl={m.photo_url} color={m.avatar_color} size={32}/>
                      <div>
                        <div className="text-[#F0EDE8] flex items-center gap-2">
                          {m.name}
                          {isSelf && <span className="text-[9px] uppercase tracking-widest text-[#C9A84C] border border-[#C9A84C]/35 rounded-full px-2 py-0.5">You</span>}
                        </div>
                        <div className="text-xs text-[#9A9590]">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-[#9A9590]">{m.sector}</td>
                  <td className="px-5 py-4">
                    <select value={m.group_role} onChange={(e)=>setGroupRole(m,e.target.value)} className="bg-transparent border border-[#C9A84C]/25 rounded px-2 py-1 text-xs text-[#F0EDE8] focus:outline-none focus:border-[#C9A84C]">
                      {GROUP_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <select value={m.role} onChange={(e)=>setRole(m,e.target.value)} disabled={isSelf} className="bg-transparent border border-[#C9A84C]/25 rounded px-2 py-1 text-xs text-[#F0EDE8] focus:outline-none focus:border-[#C9A84C] disabled:opacity-50">
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs uppercase tracking-widest ${m.status === "active" ? "text-[#C9A84C]" : "text-[#9A9590]"}`}>{m.status}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setEditing(m)} className="text-xs text-[#9A9590] hover:text-[#C9A84C] inline-flex items-center gap-1 transition" data-testid={`edit-${m.id}`}>
                        <Pencil size={12}/> Edit
                      </button>
                      <button onClick={()=>toggleStatus(m)} className="text-xs text-[#9A9590] hover:text-[#C9A84C] inline-flex items-center gap-1 transition" data-testid={`toggle-${m.id}`}>
                        {m.status === "active" ? (<><Pause size={12}/> Leave</>) : (<><Play size={12}/> Reactivate</>)}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(m)}
                        disabled={isSelf}
                        className="text-xs text-[#9A9590] hover:text-red-400 inline-flex items-center gap-1 transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title={isSelf ? "You can't delete your own account" : "Delete permanently"}
                        data-testid={`delete-${m.id}`}
                      >
                        <Trash2 size={12}/> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showInvite && <InviteModal onClose={()=>setShowInvite(false)} onCreated={load}/>}
      {editing && <EditModal member={editing} onClose={()=>setEditing(null)} onSaved={load}/>}
      {confirmDelete && (
        <ConfirmDelete
          member={confirmDelete}
          onConfirm={onDelete}
          onClose={()=>setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

function ConfirmDelete({ member, onConfirm, onClose }) {
  const [typed, setTyped] = useState("");
  const expected = (member.name || "").trim();
  const matches = typed.trim() === expected;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-7 max-w-md w-full" onClick={(e)=>e.stopPropagation()} data-testid="confirm-delete">
        <div className="overline text-red-400">Permanent delete</div>
        <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2">Remove {member.name}?</h2>
        <p className="text-sm text-[#9A9590] mt-3 leading-relaxed">
          This permanently deletes the member, their notifications, and their application record.
          Their posted opportunities and deals will remain but show as <span className="italic">unknown member</span>.
          If you just want a temporary break, use <strong className="text-[#F0EDE8]">Put on leave</strong> instead.
        </p>
        <div className="mt-5">
          <label className="overline">Type <span className="text-[#C9A84C]">{expected}</span> to confirm</label>
          <input
            className="ts-input mt-2"
            value={typed}
            onChange={(e)=>setTyped(e.target.value)}
            autoFocus
            data-testid="delete-confirm-input"
          />
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="text-sm text-[#9A9590] hover:text-[#F0EDE8]">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!matches}
            className="rounded-md px-5 py-2.5 text-sm bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition inline-flex items-center gap-2"
            data-testid="delete-confirm-submit"
          >
            <Trash2 size={14}/> Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ member, onClose, onSaved }) {
  const [f, setF] = useState({
    name: member.name || "",
    email: member.email || "",
    sector: member.sector || "",
    sub_specialties: (member.sub_specialties || []).join(", "),
    typical_client: member.typical_client || "",
    what_they_bring: member.what_they_bring || "",
    what_they_seek: member.what_they_seek || "",
    whatsapp: member.whatsapp || "",
    photo_url: member.photo_url || "",
    group_role: member.group_role || "Committed Member",
    role: member.role || "member",
    status: member.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await api.patch(`/members/${member.id}`, {
        ...f,
        sub_specialties: f.sub_specialties.split(",").map(s=>s.trim()).filter(Boolean),
      });
      onClose();
      onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-7 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="edit-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">Edit member</div>
            <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2">{member.name}</h2>
          </div>
          <button onClick={onClose} className="text-[#9A9590] hover:text-[#F0EDE8]" data-testid="edit-close"><X size={18}/></button>
        </div>
        <form onSubmit={save} className="space-y-5" data-testid="edit-form">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">Name</label><input className="ts-input" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})}/></div>
            <div><label className="overline">Email</label><input type="email" className="ts-input" value={f.email} disabled/></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">Sector</label>
              <select className="ts-input" value={f.sector} onChange={(e)=>setF({...f,sector:e.target.value})}>{SECTORS.map(s=><option key={s}>{s}</option>)}</select>
            </div>
            <div><label className="overline">Group role</label>
              <select className="ts-input" value={f.group_role} onChange={(e)=>setF({...f,group_role:e.target.value})}>{GROUP_ROLES.map(r=><option key={r}>{r}</option>)}</select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">Access</label>
              <select className="ts-input" value={f.role} onChange={(e)=>setF({...f,role:e.target.value})}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div><label className="overline">Status</label>
              <select className="ts-input" value={f.status} onChange={(e)=>setF({...f,status:e.target.value})}>
                <option value="active">Active</option>
                <option value="on_leave">On leave</option>
              </select>
            </div>
          </div>
          <div><label className="overline">Sub-specialties (comma separated)</label><input className="ts-input" value={f.sub_specialties} onChange={(e)=>setF({...f,sub_specialties:e.target.value})}/></div>
          <div><label className="overline">Typical client</label><input className="ts-input" value={f.typical_client} onChange={(e)=>setF({...f,typical_client:e.target.value})}/></div>
          <div><label className="overline">What they bring</label><textarea className="ts-input min-h-[80px] py-2 resize-none" value={f.what_they_bring} onChange={(e)=>setF({...f,what_they_bring:e.target.value})}/></div>
          <div><label className="overline">What they're looking for</label><textarea className="ts-input min-h-[80px] py-2 resize-none" value={f.what_they_seek} onChange={(e)=>setF({...f,what_they_seek:e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">WhatsApp</label><input className="ts-input" value={f.whatsapp} onChange={(e)=>setF({...f,whatsapp:e.target.value})}/></div>
          </div>
          <div>
            <label className="overline">Profile photo</label>
            <div className="mt-3">
              <FileUpload
                endpoint={`/uploads/member/${member.id}/photo`}
                accept="image/jpeg,image/png,image/webp"
                maxBytes={5 * 1024 * 1024}
                previewUrl={f.photo_url}
                variant="avatar"
                onUploaded={(rec) => setF({ ...f, photo_url: `/api/uploads/files/${rec.id}` })}
                dataTestid="edit-photo-upload"
              />
            </div>
          </div>
          {err && <div className="text-sm text-red-400/90">{err}</div>}

          <ResetPasswordSection memberId={member.id} memberName={member.name} whatsapp={member.whatsapp} />

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-sm text-[#9A9590] hover:text-[#F0EDE8]">Cancel</button>
            <button type="submit" disabled={saving} className="btn-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="edit-save">
              <Save size={14}/> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordSection({ memberId, memberName, whatsapp }) {
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState("");
  const [copied, setCopied] = useState(false);

  const reset = async () => {
    if (!window.confirm(`Reset password for ${memberName}? Their current password will stop working immediately.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/members/${memberId}/reset-password`);
      setTemp(data.temp_password);
    } catch (e) {
      alert(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(temp);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard blocked — user can select manually
    }
  };

  const waUrl = whatsapp
    ? `https://wa.me/${whatsapp.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
        `Hi ${memberName}, your Synergy Table password has been reset.\n\nTemporary password: ${temp}\n\nLogin at https://synergy-table.emergent.host and change it under Profile → Security.`
      )}`
    : "";

  return (
    <div className="border-t border-[#C9A84C]/15 pt-5" data-testid="reset-password-section">
      <div className="overline mb-2">Reset password</div>
      {!temp ? (
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-[#9A9590] leading-relaxed">
            Generates a fresh temporary password. Shown once — copy it before closing.
          </p>
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            className="btn-ghost-gold rounded-md px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
            data-testid="reset-password-btn"
          >
            {busy ? <Loader2 size={12} className="animate-spin"/> : <KeyRound size={12}/>}
            {busy ? "Resetting…" : "Reset password"}
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="temp-password-display">
          <div className="flex items-center gap-3">
            <code className="flex-1 font-mono text-base text-[#C9A84C] bg-[#0D0D0D] border border-[#C9A84C]/40 rounded-md px-4 py-3 select-all break-all">
              {temp}
            </code>
            <button
              type="button"
              onClick={copy}
              className="btn-ghost-gold rounded-md px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 shrink-0"
              data-testid="copy-temp-btn"
            >
              {copied ? <Check size={12}/> : <Copy size={12}/>}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {whatsapp && (
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] hover:text-[#E1C97A] transition inline-flex items-center gap-1.5"
              data-testid="share-wa-btn"
            >
              Share via WhatsApp ↗
            </a>
          )}
          <p className="text-[10px] uppercase tracking-widest text-amber-400/70">
            This is shown once. Closing or refreshing wipes it.
          </p>
        </div>
      )}
    </div>
  );
}

function generateTempPassword() {
  // Cryptographically-strong 12-char temp password the admin shares once.
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
  const arr = new Uint32Array(12);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr, (n) => alphabet[n % alphabet.length]).join("");
}

function InviteModal({ onClose, onCreated }) {
  const [f, setF] = useState(() => ({
    name: "", email: "", sector: SECTORS[0], group_role: "Committed Member",
    sub_specialties: "", what_they_bring: "", what_they_seek: "", typical_client: "",
    whatsapp: "", password: generateTempPassword(),
  }));
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/members", {
        ...f,
        sub_specialties: f.sub_specialties.split(",").map(s=>s.trim()).filter(Boolean),
      });
      onClose(); onCreated();
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Failed to invite.");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="overline">Invite</div>
        <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2 mb-6">Add to the table</h2>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">Name</label><input required className="ts-input" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})}/></div>
            <div><label className="overline">Email</label><input required type="email" className="ts-input" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})}/></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">Sector</label>
              <select className="ts-input" value={f.sector} onChange={(e)=>setF({...f,sector:e.target.value})}>{SECTORS.map(s=><option key={s}>{s}</option>)}</select>
            </div>
            <div><label className="overline">Group role</label>
              <select className="ts-input" value={f.group_role} onChange={(e)=>setF({...f,group_role:e.target.value})}>{GROUP_ROLES.map(r=><option key={r}>{r}</option>)}</select>
            </div>
          </div>
          <div><label className="overline">Sub-specialties (comma)</label><input className="ts-input" value={f.sub_specialties} onChange={(e)=>setF({...f,sub_specialties:e.target.value})}/></div>
          <div><label className="overline">What they bring</label><textarea className="ts-input min-h-[60px] py-2" value={f.what_they_bring} onChange={(e)=>setF({...f,what_they_bring:e.target.value})}/></div>
          <div><label className="overline">What they're looking for</label><textarea className="ts-input min-h-[60px] py-2" value={f.what_they_seek} onChange={(e)=>setF({...f,what_they_seek:e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">WhatsApp</label><input className="ts-input" value={f.whatsapp} onChange={(e)=>setF({...f,whatsapp:e.target.value})}/></div>
            <div><label className="overline">Temp password</label><input className="ts-input" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})}/></div>
          </div>
          {error && <div className="text-sm text-red-400/90">{error}</div>}
          <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={onClose} className="text-sm text-[#9A9590]">Cancel</button><button type="submit" className="btn-gold rounded-md px-5 py-2.5 text-sm">Send invite</button></div>
        </form>
      </div>
    </div>
  );
}
